import { useMemo, useState } from 'react';

import sort from 'lodash-es/sortBy';
import { BoxIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { StyledBadge } from '@/components/atoms/StyledBadge/StyledBadge';
import { CollapsibleNavGroupHeader } from '@/components/organisms/Navbar/CollapsibleNavGroupHeader/CollapsibleNavGroupHeader';
import SubNavShell from '@/components/organisms/Navbar/SubNavShell/SubNavShell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAccount } from '@/hooks/useAccount';
import useDesks from '@/hooks/useDesks';
import { useInvitations } from '@/hooks/useInvitations';
import { usePermissions } from '@/hooks/usePermissions';
import { safeLocalStorageRead, safeLocalStorageWrite } from '@/lib/storage/localStorage';
import { cn } from '@/lib/utils';
import { useCanReadUsage, useHasVoiceCalls } from '@/modules/billing/hooks/useVoiceUsage';
import { selectReadableDeveloperModules } from '@/modules/developerTools/selectors';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { RoleType } from '@/shared/types/member';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { personalSettings, accountSettings, developerSettings, SettingsNavItem } from './constants';

const SECTION_KEYS = {
  account: 'settings-nav:account-expanded',
  personal: 'settings-nav:personal-expanded',
  developers: 'settings-nav:developers-expanded',
  environments: 'settings-nav:environments-expanded',
} as const;

type SectionKey = keyof typeof SECTION_KEYS;

const BADGE_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'right' } as const;

const readExpanded = (key: string) => {
  const raw = safeLocalStorageRead(key);
  return raw === null ? true : raw === 'true';
};

const navItemClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex w-full min-w-0 items-center gap-2 rounded border border-transparent px-2 py-1 text-left text-sm text-foreground transition-colors hover:bg-selected-soft',
    isActive && 'border-border bg-background'
  );

interface SettingsNavGroupProps {
  label: string;
  items: SettingsNavItem[];
  isExpanded: boolean;
  onToggle: () => void;
}

const SettingsNavGroup = ({ label, items, isExpanded, onToggle }: SettingsNavGroupProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="mb-1">
      <CollapsibleNavGroupHeader label={label} isExpanded={isExpanded} onToggle={onToggle} />
      {isExpanded && (
        <div className="mt-1 ml-3 space-y-0.5 border-l border-border pl-3">
          {items.map(({ title_key, link, icon }) => (
            <NavLink to={`/${slug}${link}`} key={title_key} className={navItemClass}>
              <span className="h-4 w-4 shrink-0 text-muted-foreground">{icon}</span>
              <span className="min-w-0 truncate">{t(title_key)}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export const SettingsNavigation = () => {
  const { slug, accountRole, account, accountStatus, accountsStatus } = useAccount();
  const { hasInvitations } = useInvitations();
  const { handleCreateDesk, desks, maxDesksReached, isLoadingDesks } = useDesks();
  const sortedDesks = useMemo(() => sort(desks, ['name']), [desks]);
  const { t } = useTranslation();
  const { permissions, listStatus } = usePermissions();

  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>(() => ({
    account: readExpanded(SECTION_KEYS.account),
    personal: readExpanded(SECTION_KEYS.personal),
    developers: readExpanded(SECTION_KEYS.developers),
    environments: readExpanded(SECTION_KEYS.environments),
  }));

  const toggleSection = (key: SectionKey) => {
    const next = !expanded[key];
    setExpanded((prev) => ({ ...prev, [key]: next }));
    safeLocalStorageWrite(SECTION_KEYS[key], String(next));
  };

  const canReadBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.READ)
  );
  const { billing_usage } = useSelector(selectFeatureFlags);
  const canReadUsage = useCanReadUsage() && billing_usage === true;
  // Hidden once the check finds no carrier calls or fails, never while it loads.
  const hasVoiceCalls = useHasVoiceCalls({ skip: !canReadUsage });
  const canWrite = useSelector((state: RootState) => getPermissions(state, 'desks', actions.WRITE));
  const canReadDesks = useSelector((state: RootState) =>
    getPermissions(state, 'desks', actions.READ)
  );
  const canReadRoles = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.READ)
  );
  const canReadTeams = useSelector((state: RootState) =>
    getPermissions(state, 'teams', actions.READ)
  );
  const canUpdateAccount = useSelector((state: RootState) =>
    getPermissions(state, 'accounts', actions.UPDATE)
  );
  const readableDeveloperModules = useSelector(selectReadableDeveloperModules);
  const isOwner = accountRole === RoleType.OWNER;

  const canSeeAuditLogs =
    isOwner ||
    permissions?.find(({ permission, allow }) => permission === 'audit_logs.read' && allow);

  const isLoadingNav =
    isLoadingDesks ||
    accountsStatus === 'pending' ||
    (!!slug && accountStatus === 'pending') ||
    (!!account && listStatus === 'pending');

  if (isLoadingNav) {
    return (
      <SubNavShell disableHover>
        <Skeleton className="h-96 w-full" />
      </SubNavShell>
    );
  }

  const disableCreateDesk = maxDesksReached || !canWrite;
  let disableCreateDeskTooltip: string | undefined;
  if (disableCreateDesk) {
    disableCreateDeskTooltip = maxDesksReached
      ? t('limits.desks_reached')
      : t('desks.no_permission');
  }

  const settingsVisibility: Record<string, boolean> = {
    'common.general': canUpdateAccount,
    'pages.billing_simple': canReadBilling,
    'pages.invoices': canReadBilling,
    'pages.usage': canReadUsage && hasVoiceCalls !== false,
    'permissions.manage_access': canReadRoles || canReadTeams,
    'pages.human_agents': canUpdateAccount,
    'pages.activity_logs': !!canSeeAuditLogs,
    'common.data_privacy': isOwner,
  };

  const allowedSettings = accountSettings.filter(
    ({ title_key }) => settingsVisibility[title_key] ?? true
  );

  const allowedDeveloperSettings = developerSettings.filter(({ module }) =>
    readableDeveloperModules.includes(module)
  );

  return (
    <SubNavShell>
      <SettingsNavGroup
        label={t('profile.account_settings')}
        items={allowedSettings}
        isExpanded={expanded.account}
        onToggle={() => toggleSection('account')}
      />

      {/* Personal */}
      <div className="mb-1">
        <CollapsibleNavGroupHeader
          label={t('profile.personal_settings')}
          isExpanded={expanded.personal}
          onToggle={() => toggleSection('personal')}
        />
        {expanded.personal && (
          <div className="mt-1 ml-3 space-y-0.5 border-l border-border pl-3">
            {personalSettings.map(({ title_key, icon, link, showBadge }) => (
              <NavLink to={link} key={title_key} className={navItemClass}>
                {showBadge ? (
                  <StyledBadge
                    className="!mr-0 !ml-0 h-4 w-4 shrink-0"
                    status="away"
                    invisible={!hasInvitations}
                    anchorOrigin={BADGE_ANCHOR_ORIGIN}
                  >
                    <span className="h-4 w-4 text-muted-foreground">{icon}</span>
                  </StyledBadge>
                ) : (
                  <span className="h-4 w-4 shrink-0 text-muted-foreground">{icon}</span>
                )}
                <span className="min-w-0 truncate">{t(title_key)}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>

      <SettingsNavGroup
        label={t('developer_tools.developers')}
        items={allowedDeveloperSettings}
        isExpanded={expanded.developers}
        onToggle={() => toggleSection('developers')}
      />

      {/* Environments */}
      {canReadDesks && (
        <div>
          <CollapsibleNavGroupHeader
            label={t('common.environments')}
            isExpanded={expanded.environments}
            onToggle={() => toggleSection('environments')}
            action={
              <InfoTooltip content={disableCreateDeskTooltip}>
                <span>
                  <Button
                    variant="ghost-neutral"
                    size="icon-sm"
                    onClick={handleCreateDesk}
                    disabled={disableCreateDesk}
                    aria-label={t('environments.create')}
                  >
                    <PlusIcon size={16} />
                  </Button>
                </span>
              </InfoTooltip>
            }
          />

          {expanded.environments && (
            <div className="mt-1 ml-3 space-y-0.5 border-l border-border pl-3">
              {sortedDesks.length > 0 ? (
                sortedDesks.map(({ name, desk_id }) => (
                  <NavLink
                    to={`/${slug}/environments/${desk_id}`}
                    key={desk_id}
                    className={navItemClass}
                  >
                    <BoxIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 truncate">{name}</span>
                  </NavLink>
                ))
              ) : (
                <p className="px-2 py-1 text-sm text-muted-foreground">
                  {t('environments.no_env_yet')}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </SubNavShell>
  );
};
